import { ReactComponent as IconErrorHint } from "@/assets/svgs/error-hint.svg";
import { useHealthdex } from "@/hooks/use-healthdex/index";
import useEForm from '@/hooks/use-eform/index';
import { useFeature } from '@/hooks/use-feature';
import { featureConstant } from '@/constants/configuration/feature';
import addFormats from "@/utils/custom-ajv-formats";
import Ajv from "ajv";
import className from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import { extractPreviousValueFromPreviousPayload, parentPropsToChangedField } from '../EForm/eFormReadMode';
import FormField from './EFormFieldDictionary';
import InfoIcon from './InfoIcon';
import { parentPropsToPath, isPathLocked } from '@/utils/eform-schema-paths';

const ajv = new Ajv({ allErrors: true, verbose: true });
addFormats(ajv);

const FormLabel = ({ required, label, description, propertyName }) => (
    <div className="eform-label-container">
        {required && <span className="eformv2-label-asterisk" data-testid="eform-label-asterisk">*</span>}
        <div className="eformv2-label" data-testid={`eformv2-label-${propertyName}`} id={propertyName}>{label}</div>
        <InfoIcon content={description} />
    </div>
);

FormLabel.propTypes = {
    required: PropTypes.bool,
    label: PropTypes.string,
    description: PropTypes.string,
    propertyName: PropTypes.string
};

const ErrorHint = ({ label, validateErrors }) => {
    // Get the error message based on the validation function
    const errorMessage = validateErrors(label);
    
    return (
        <div style={{ display: 'flex', justifyItems: 'start', justifyContent: 'start', alignItems: 'center', color: '#a72e12', marginTop: '0.3rem' }}>
            <IconErrorHint />
            <div style={{ marginLeft: '5px' }}>{errorMessage}</div>
        </div>
    );
};

ErrorHint.propTypes = {
    label: PropTypes.string,
    validateErrors: PropTypes.func,
};

const BaseEForm = ({ schema, required, parentProps = [], payload, setPayload, isReadOnly, previousPayload, changedFields, formHandlers, selectedModelTerms, setSelectedModelTerms }) => {
    const { isDirty, errorListToDisplay, setIsValidFormat, isSubmitClicked } = formHandlers;
    const [eFormFieldError, setEFormFieldError] = useState(false);
    const { getFieldValue, getNextPayload } = useEForm();
    const [inputProps, setInputProps] = useState(null);
    const previousValue = previousPayload && changedFields?.includes(parentPropsToChangedField(parentProps)) ?
        (extractPreviousValueFromPreviousPayload(previousPayload, parentProps) ?? '') :
        null;
    const propertyName = parentProps[parentProps.length - 1];

    const isHealthDex = useHealthdex();
    const isErrorMessageEnhancement = useFeature(featureConstant.ERROR_MESSAGE_ENHANCEMENT);
    const isSubmittalsEnabled = useFeature(featureConstant.SUBMITTALS);

    // A field is read-only when the page is read-only or, when SUBMITTALS is enabled,
    // when its path is auto-filled by a sibling dynamic-select or when its path
    // (or an ancestor) is in lockedPaths via the section's editable-statuses.
    const isFieldLockedBySubmittals = useMemo(() => {
        if (!isSubmittalsEnabled) return false;
        const { autoFilledPaths, lockedPaths } = formHandlers;
        const fieldPath = parentPropsToPath(parentProps);
        if (autoFilledPaths instanceof Set && autoFilledPaths.has(fieldPath)) return true;
        if (isPathLocked(fieldPath, lockedPaths)) return true;
        return false;
    }, [isSubmittalsEnabled, formHandlers.autoFilledPaths, formHandlers.lockedPaths, parentProps]);

    const effectiveIsReadOnly = isReadOnly || isFieldLockedBySubmittals;

    const schemaType = schema["x-uitype"] ?? schema.type;
    const currentType = Array.isArray(schemaType) ? schemaType.find((d) => d !== "null") : schemaType;
    const label = schema.title ?? schema.description;
    const [touched, setTouched] = useState(false);
    // Add useMemo for hasErrorsForField like in EFormFieldComponent - MOVED UP to avoid conditional hook call
    const hasErrorsForField = useMemo(() => {
        if (!errorListToDisplay) return false;
        
        // Direct match (for non-array fields)
        if (Object.keys(errorListToDisplay).includes(propertyName)) {
            return true;
        }

        // For array items, we need to be more specific about which array item we're checking
        if (parentProps && parentProps.length > 0) {
            // Check if we're in an array item by examining parent props
            const isInArrayItem = parentProps.some((prop, index) => 
                !isNaN(prop) && index > 0 && isNaN(parentProps[index - 1])
            );
            
            if (isInArrayItem) {
                // Construct the exact path for this specific array item
                const fullPath = parentProps.join('/');
                const specificFieldPath = `${fullPath}/${propertyName}`;
                
                // Only return true if this specific array item's field has an error
                return Object.keys(errorListToDisplay).some(key => 
                    key === specificFieldPath || key === fullPath
                );
            } else {
                // For non-array nested fields, check if any path contains all parent props in order
                const fullPath = parentProps.join('/');
                return Object.keys(errorListToDisplay).some(key => 
                    key.includes(fullPath) && key.endsWith(propertyName)
                );
            }
        }

        // For top-level fields, only exact matches should count
        return false;
    }, [errorListToDisplay, propertyName, parentProps]);

    const shouldShowErrorCaption = useMemo(
        () =>
            isErrorMessageEnhancement &&
            hasErrorsForField &&
            (isSubmitClicked || touched),
        [
            errorListToDisplay,
            isSubmitClicked,
            isErrorMessageEnhancement,
            propertyName,
            touched,
            hasErrorsForField
        ]
    );

    useEffect(() => {
        // Show validation errors when form is submitted or when field is dirty
        if ((isDirty || isSubmitClicked) && inputProps?.value !== undefined) {
            validateSchema(inputProps.value);
        }
    }, [isDirty, isSubmitClicked, inputProps?.value]);
    
    if(isReadOnly && schemaType === 'file-upload') return null;

    function updatePayload(value) {
        if (!touched) {
            setTouched(true);
        }
        
        // Convert empty strings to null for non-mandatory fields
        // Check if the field is non-mandatory by looking at schema type
        const isNullableField = Array.isArray(schema.type) && schema.type.includes('null');
        
        // If field is nullable and value is empty string, set it to null
        const processedValue = isNullableField && value === '' ? null : value;
        
        const nextPayload = getNextPayload(payload, parentProps, processedValue);
        setPayload(nextPayload);
        return nextPayload;
    }

    // Commits the field value plus updates at the given dot-paths in a single
    // setPayload call. Used by `dynamic-select` to write the selection and any
    // sibling/cross-section auto-fills declared in `x-uioptions.fields-to-populate`.
    function updatePayloadWithSiblings(value, siblings) {
        if (!touched) setTouched(true);
        let nextPayload = getNextPayload(payload, parentProps, value);
        if (siblings) {
            for (const [path, sibValue] of Object.entries(siblings)) {
                nextPayload = getNextPayload(nextPayload, path.split('.'), sibValue);
            }
        }
        setPayload(nextPayload);
        return nextPayload;
    }

    function validateSchema(value) {
        if ((isHealthDex && isDirty) || !isHealthDex) {
            const validate = ajv.compile(schema);

            const valueToValidate = value === ""? null : value;

            const validateResult = validate(valueToValidate);
            
            // Set error state based on validation result
            setEFormFieldError(!validateResult);
            
            return validateResult;
        }
        return true;
    }

    // hasErrorsForField is defined at the top of the component
    
    function validateErrors(fieldTitle) {
        // Don't show errors for fields with blank names
        if (!fieldTitle || fieldTitle.trim() === '') {
            return '';
        }
        
        // Implement getErrorKey function from EFormFieldComponent
        const getErrorKey = () => {
            // Direct match
            if (errorListToDisplay[propertyName]) {
                return propertyName;
            }
            
            // Full path match
            const fullPath = parentProps.join('/');
            if (errorListToDisplay[fullPath]) {
                return fullPath;
            }
            
            // For array items
            if (parentProps.length >= 2) {
                const arrayName = parentProps[parentProps.length - 2];
                const arrayIndex = parentProps[parentProps.length - 1];
                
                if (!isNaN(arrayIndex)) {
                    const path = `${arrayName}/${arrayIndex}/${propertyName}`;
                    if (errorListToDisplay[path]) {
                        return path;
                    }
                }
            }
            
            // Look for paths ending with propertyName
            const matchingKeys = Object.keys(errorListToDisplay).filter(key => {
                const parts = key.split('/');
                return parts[parts.length - 1] === propertyName;
            });
            
            if (matchingKeys.length > 0) {
                return matchingKeys[0];
            }
            
            return null;
        };
        
        // For enhanced error mode, use the same logic as EFormFieldComponent with conditional error handling
        if (isErrorMessageEnhancement) {
            const errorKey = getErrorKey();
            
            // Safety checks exactly as in EFormFieldComponent
            if (!errorKey || !errorListToDisplay[errorKey] || errorListToDisplay[errorKey].length === 0) {
                return fieldTitle + " is not valid";
            }
            
            if (!Array.isArray(errorListToDisplay[errorKey])) {
                return fieldTitle + " is not valid";
            }
            
            // Use the first error message directly without conditional filtering
            const errorMessages = errorListToDisplay[errorKey];
            const errorToUse = errorMessages[0];
            
            // Extract error message without field title, exactly as in EFormFieldComponent
            const parts = errorToUse.split(':');
            if (parts.length >= 2) {
                const errorPart = parts.slice(1).join(':').trim();
                return errorPart;
            }
            
            return errorToUse;
        }
        
        // Default fallback
        return fieldTitle + " is not valid";
    }

    const cellClassName = className(
        {
            "eform-read-mode__field-value": isReadOnly,
            "eform-read-mode__field-value--changed": isReadOnly && previousValue !== null,
        }
    )
    
    const props = {
        error: eFormFieldError,
        isReadOnly: effectiveIsReadOnly,
        schema,
        payload,
        parentProps,
        inputProps,
        setInputProps,
        updatePayload,
        updatePayloadWithSiblings,
        validateSchema,
        getFieldValue,
        setEFormFieldError,
        setIsValidFormat,
        required,
        propertyName,
        selectedModelTerms,
        setSelectedModelTerms
    };

    
    return (
        <div data-testid="eform-row">
            {currentType === "dynamic-dropdown" && <div className="etr-igpni__eform__model-terms">{label}</div>}
            <div className={cellClassName} data-testid={`field-value-${propertyName}`} style={currentType !== "dynamic-dropdown" ? { width: '95%' } : undefined}>
                <FormLabel required={required} label={currentType === "dynamic-dropdown" ? `List of Templates` : label} description={schema.title ? schema.description : null} propertyName={propertyName}/>
                <FormField type={currentType} schema={schema} {...props} />
            </div>
            
            {/* Error display exactly matching EFormFieldComponent */}
            {eFormFieldError && !isErrorMessageEnhancement && (
                <div className="eform-input-error flex-box flex-align-center">
                    <IconErrorHint />
                    {!isHealthDex && <div>{label} is not valid</div>}
                    {isHealthDex && <div>{validateErrors(label)}</div>}
                </div>
            )}
            
            {shouldShowErrorCaption &&
                <div className="eform-input-error flex-box flex-align-center">
                    <IconErrorHint />
                    <div>{validateErrors(label)}</div>
                </div>
            }
            
            {isReadOnly && previousValue !== null && currentType !== "dynamic-dropdown" && (
                <div className="eform-read-mode__previous-value">
                    Previous value: {previousValue}
                </div>
            )}
        </div>
    )
}

BaseEForm.propTypes = {
    schema: PropTypes.object,
    required: PropTypes.bool,
    parentProps: PropTypes.array,
    payload: PropTypes.object,
    setPayload: PropTypes.func,
    isReadOnly: PropTypes.bool,
    previousPayload: PropTypes.object,
    changedFields: PropTypes.array,
    formHandlers: PropTypes.shape({
        isReadOnly: PropTypes.bool,
        isDirty: PropTypes.bool,
        errorListToDisplay: PropTypes.array,
        setIsValidFormat: PropTypes.func,
        autoFilledPaths: PropTypes.instanceOf(Set),
        lockedPaths: PropTypes.instanceOf(Set)
    }),
    selectedModelTerms: PropTypes.string,
    setSelectedModelTerms: PropTypes.func
};

export default BaseEForm;
